import React, { useMemo, useState } from 'react';
import DashboardPieChart from './charts/DashboardPieChart';
import { EMAIL_STATS_METRICS, EMAIL_STATS_WINDOWS, SYSTEM_EMAIL_STATS_CATEGORIES, emailStatKey } from '../dashboardStatsConfig';
import { parseStatValue } from '../dashboardStatUtils';
import { PATIENT_STATS_PALETTE } from '../dashboardChartColors';

const chartColor = (index) => PATIENT_STATS_PALETTE[index % PATIENT_STATS_PALETTE.length];

function DashboardSystemEmailStats({ getStat, isLoading, loadError, lastUpdated }) {
    const [days, setDays] = useState(30);
    const [metric, setMetric] = useState('sent');

    const sentSeries = useMemo(
        () =>
            SYSTEM_EMAIL_STATS_CATEGORIES.map((category, index) => ({
                key: category.keyPrefix,
                label: category.label,
                color: chartColor(index),
                value: parseStatValue(getStat(emailStatKey(category.keyPrefix, 'sent', days))),
            })),
        [days, getStat]
    );

    const openedSeries = useMemo(
        () =>
            SYSTEM_EMAIL_STATS_CATEGORIES.map((category, index) => ({
                key: category.keyPrefix,
                label: category.label,
                color: chartColor(index),
                value: parseStatValue(getStat(emailStatKey(category.keyPrefix, 'opened', days))),
            })),
        [days, getStat]
    );

    const series = metric === 'opened' ? openedSeries : sentSeries;
    const hasData = series.some((item) => item.value > 0);
    const sentTotal = sentSeries.reduce((sum, item) => sum + item.value, 0);
    const openedTotal = openedSeries.reduce((sum, item) => sum + item.value, 0);
    const total = metric === 'opened' ? openedTotal : sentTotal;
    const openRate = sentTotal > 0 ? Math.round((openedTotal * 100) / sentTotal) : 0;

    return (
        <section className="ecx-page-card ecx-page-panel dashboard-stats-panel dashboard-panel-theme dashboard-panel-theme--green">
            <div className="ecx-page-panel__header">
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="dashboard-stats-panel__heading">
                        <span className="ecx-page-icon-badge">
                            <i className="bi bi-gear" />
                        </span>
                        <div>
                            <h2 className="dashboard-stats-panel__title">System Generated Emails</h2>
                            <p className="dashboard-stats-panel__subtitle mb-0">
                                {lastUpdated
                                    ? `EyeconX emails ${metric} for this clinic · Updated ${lastUpdated}`
                                    : 'EyeconX-generated emails sent and opened for this clinic'}
                            </p>
                        </div>
                    </div>
                    <div className="dashboard-email-stats__toggles">
                        <div className="dashboard-email-stats__toggle dashboard-email-stats__toggle--metric" role="group" aria-label="System email stats metric">
                            {EMAIL_STATS_METRICS.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={`dashboard-email-stats__toggle-btn${metric === item.id ? ' is-active' : ''}`}
                                    onClick={() => setMetric(item.id)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        <div className="dashboard-email-stats__toggle" role="group" aria-label="System email stats period">
                            {EMAIL_STATS_WINDOWS.map((window) => (
                                <button
                                    key={window.days}
                                    type="button"
                                    className={`dashboard-email-stats__toggle-btn${days === window.days ? ' is-active' : ''}`}
                                    onClick={() => setDays(window.days)}
                                >
                                    {window.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="ecx-page-panel__body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-exclamation-circle me-2" />
                        {loadError}
                    </div>
                )}

                {isLoading ? (
                    <div className="dashboard-stat-tile dashboard-stat-tile--skeleton" style={{ minHeight: 280 }} />
                ) : !hasData ? (
                    <div className="dashboard-empty-state">
                        <i className="bi bi-pie-chart me-2" />
                        {metric === 'opened'
                            ? `No opened system-generated emails in the last ${days} days`
                            : `No system-generated emails in the last ${days} days`}
                    </div>
                ) : (
                    <>
                        <p className="dashboard-email-stats__total mb-3">
                            Total {metric}: <strong>{total.toLocaleString()}</strong>
                            {' · '}
                            Open rate: <strong>{openRate}%</strong>
                        </p>
                        <DashboardPieChart series={series} />
                    </>
                )}
            </div>
        </section>
    );
}

export default DashboardSystemEmailStats;
